import { Link } from "react-router-dom";
import type { Video } from "@/types/video";
import { formatViews } from "@/utils/format";

interface HeroBannerProps {
  main: Video;
  side: Video[];
}

export default function HeroBanner({ main, side }: HeroBannerProps) {
  return (
    <section className="relative w-full pt-16">
      <div className="relative w-full h-[440px] md:h-[620px] overflow-hidden">
        <img
          src={main.cover}
          alt={`${main.title} - 日皮视频焦点推荐`}
          title={`${main.title} 影视视频`}
          className="absolute inset-0 w-full h-full object-cover object-top animate-kenburns"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/55 to-black/20" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />

        <div className="relative w-full h-full px-4 md:px-8 flex flex-col lg:flex-row items-start lg:items-center gap-8 pt-12 lg:pt-0">
          <div className="w-full lg:flex-1 max-w-2xl">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary-500 text-background-50 text-xs font-medium mb-4">
              <i className="ri-fire-fill" /> 今日焦点推荐
            </span>
            <h2 className="font-heading font-black text-3xl md:text-5xl text-background-50 leading-tight">
              {main.title}
            </h2>
            <p className="mt-4 text-sm md:text-base text-background-200 leading-relaxed max-w-xl line-clamp-3">
              {main.description}
            </p>
            <div className="mt-5 flex items-center gap-4 text-xs md:text-sm text-background-300 flex-wrap">
              <span className="flex items-center gap-1 whitespace-nowrap">
                <i className="ri-price-tag-3-line" /> {main.categoryName}
              </span>
              <span className="flex items-center gap-1 whitespace-nowrap">
                <i className="ri-time-line" /> {main.duration}
              </span>
              <span className="flex items-center gap-1 whitespace-nowrap">
                <i className="ri-eye-line" /> {formatViews(main.views)}
              </span>
            </div>
            <div className="mt-7 flex items-center gap-3 flex-wrap">
              <Link
                to={`/detail/${main.id}`}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium whitespace-nowrap cursor-pointer transition-colors"
              >
                <i className="ri-play-circle-fill text-lg" /> 立即观看
              </Link>
              <Link
                to={`/category/${main.category}`}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-md border border-background-50/40 text-background-50 hover:bg-background-50/10 text-sm font-medium whitespace-nowrap cursor-pointer transition-colors"
              >
                查看更多{main.categoryName}
              </Link>
            </div>
          </div>

          <aside className="hidden lg:block w-full max-w-sm shrink-0">
            <div className="rounded-lg bg-black/45 backdrop-blur-sm border border-white/10 p-5">
              <h3 className="text-background-50 font-heading font-bold text-base mb-4 flex items-center gap-2">
                <i className="ri-flashlight-fill text-primary-400" /> 热门推荐
              </h3>
              <ul className="space-y-3">
                {side.map((item, index) => (
                  <li key={item.id}>
                    <Link
                      to={`/detail/${item.id}`}
                      className="flex items-start gap-3 group cursor-pointer"
                    >
                      <span className="w-6 h-6 shrink-0 rounded-md bg-primary-500/90 text-background-50 text-xs font-bold flex items-center justify-center mt-0.5">
                        {index + 1}
                      </span>
                      <span className="flex-1">
                        <span className="block text-sm text-background-100 leading-snug line-clamp-2 group-hover:text-primary-300 transition-colors">
                          {item.title}
                        </span>
                        <span className="mt-1 block text-xs text-background-400">
                          {item.categoryName} · {item.duration}
                        </span>
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}